import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import ScrollToTop from '../components/ScrollToTop';
import VideoPlayer from '../components/VideoPlayer';
import TeamMember from '../components/TeamMember';
import InteractiveDemo from '../components/InteractiveDemo';
import DocsNavigator from '../components/DocsNavigator';
import GitHubButton from '../components/GitHubButton';

// Import SVG as component
import { ReactComponent as ZKPhysicsSVG } from '../assets/svg/zk-physics.svg';

const PageContainer = styled.div`
  padding-top: 2rem;
`;

const HeroSection = styled.section`
  background: linear-gradient(to right, #2c3e50, #34495e);
  color: white;
  padding: 4rem 2rem;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Title = styled(motion.h1)`
  font-size: 3rem;
  margin-bottom: 1.5rem;
`;

const Subtitle = styled(motion.h2)`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  font-weight: 400;
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 3rem auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background: #2c3e50;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #2c3e50;
`;

const CardContent = styled.p`
  line-height: 1.6;
  color: #555;
`;

const DiagramContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 3rem auto;
`;

const DiagramTitle = styled.h3`
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1.8rem;
  color: #333;
`;

const AnimationContainer = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 3rem auto;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin: 2.5rem 0;
`;

const StatCard = styled(motion.div)`
  background: #2c3e50;
  color: white;
  border-radius: 10px;
  padding: 1.5rem;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  opacity: 0.8;
`;

const ZKPhysics = () => {
  const [activeDocSection, setActiveDocSection] = useState('overview');
  
  // Mock documentation sections for ZK Physics Engine
  const docSections = {
    overview: { title: 'Overview', content: '# Zero Knowledge Physics Engine\n\nA real-time, verifiable physics engine with formal verification and military-grade simulation capabilities.' },
    architecture: { title: 'Architecture', content: '# Architecture\n\nThe Zero Knowledge Physics Engine consists of three main components:\n\n1. **Physics Simulation Core**\n2. **Formal Verification Layer**\n3. **Cryptographic State Tracking**' },
    installation: { title: 'Installation', content: '# Installation\n\n```bash\nnpm install zk-physics\n```\n\nOr use Docker:\n\n```bash\ndocker pull zk-physics/core:latest\n```' },
    usage: { title: 'Usage', content: '# Usage\n\n```javascript\nconst ZKPhysics = require("zk-physics");\n\nconst simulation = new ZKPhysics.Simulation({\n  precision: "high",\n  verification: true,\n  cryptoTrack: true\n});\n\nsimulation.addBody({\n  mass: 10,\n  position: [0, 0, 0],\n  velocity: [0, 10, 0]\n});\n\nsimulation.start();\n```' }
  };
  
  // Sample API demo data
  const apiDemoData = {
    request: `{
  "simulation": "physics-test-01",
  "bodies": [
    {
      "id": "sphere-1",
      "type": "sphere",
      "mass": 10,
      "radius": 1,
      "position": [0, 10, 0],
      "velocity": [0, 0, 0]
    },
    {
      "id": "plane-1",
      "type": "plane",
      "normal": [0, 1, 0],
      "point": [0, 0, 0],
      "material": "concrete"
    }
  ],
  "duration": 10,
  "timestep": 0.01,
  "verification": true
}`,
    response: `{
  "status": "success",
  "data": {
    "simulationId": "phys-sim-28491f3a",
    "frameCount": 1000,
    "computeTime": 235.4,
    "verificationHash": "0x7a58c0b21bfF6B5C5957822f6c9f2e3D4A1e3D47",
    "finalStates": {
      "sphere-1": {
        "position": [0, 0.78, 0],
        "velocity": [0, -7.82, 0]
      }
    }
  }
}`
  };
  
  // Sample SDK demo code
  const sdkDemoCode = `// Initialize ZK Physics Engine
const ZKPhysics = require('zk-physics');

const simulation = new ZKPhysics.Simulation({
  precision: "high",
  verification: true,
  cryptoTrack: true
});

// Add physical bodies
simulation.addBody({
  id: "sphere-1",
  type: "sphere",
  mass: 10,
  radius: 1,
  position: [0, 10, 0],
  velocity: [0, 0, 0]
});

simulation.addBody({
  id: "plane-1",
  type: "plane",
  normal: [0, 1, 0],
  point: [0, 0, 0],
  material: "concrete"
});

// Run the simulation
const results = await simulation.run({
  duration: 10,
  timestep: 0.01
});

console.log(\`Verification hash: \${results.verificationHash}\`);
console.log(\`Final position: \${JSON.stringify(results.finalStates["sphere-1"].position)}\`);`;

  return (
    <PageContainer>
      <ScrollToTop background="#2c3e50" />
      <HeroSection>
        <HeroContent>
          <Title
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Zero Knowledge Physics Engine
          </Title>
          <Subtitle
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Real-time, formally verified physics simulation with military-grade precision
          </Subtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <SectionTitle>Overview</SectionTitle>
        <p>
          The Zero Knowledge Physics Engine is a groundbreaking simulation system that combines 
          high-performance real-time physics calculations with formal verification and cryptographic 
          state tracking. This unique combination makes it suitable for applications requiring both 
          speed and absolute reliability, from gaming to military simulations.
        </p>
        
        <StatsGrid>
          <StatCard
            whileHover={{ y: -10 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <StatNumber>10,000+</StatNumber>
            <StatLabel>Physics Objects</StatLabel>
          </StatCard>
          
          <StatCard
            whileHover={{ y: -10 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <StatNumber>0.1ms</StatNumber>
            <StatLabel>Computation Time</StatLabel>
          </StatCard>
          
          <StatCard
            whileHover={{ y: -10 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <StatNumber>99.999%</StatNumber>
            <StatLabel>Verification Accuracy</StatLabel>
          </StatCard>
          
          <StatCard
            whileHover={{ y: -10 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <StatNumber>60+ FPS</StatNumber>
            <StatLabel>Real-time Performance</StatLabel>
          </StatCard>
        </StatsGrid>
        
        <DiagramContainer>
          <DiagramTitle>Zero Knowledge Physics Engine Architecture</DiagramTitle>
          <ZKPhysicsSVG width="100%" height="auto" />
        </DiagramContainer>
        
        <AnimationContainer>
          <VideoPlayer 
            src="https://youtu.be/FC7uKB5mZgE" 
            title="Zero Knowledge Physics Engine"
          />
        </AnimationContainer>
        
        <GitHubButton url="https://github.com/GlobalSushrut/zero-point-verifiable-physics">
          View ZK Physics Engine on GitHub
        </GitHubButton>
        
        <SectionTitle>Interactive Demo</SectionTitle>
        <p>
          Explore the Zero Knowledge Physics Engine API and SDK. Create simulations, verify results, and see the power of formally verified physics:
        </p>
        
        <InteractiveDemo
          tabs={[
            { id: 'api', label: 'REST API', icon: 'api' },
            { id: 'sdk', label: 'JavaScript SDK', icon: 'code' },
            { id: 'cli', label: 'CLI', icon: 'terminal' }
          ]}
          defaultTab="api"
          apiRequestData={apiDemoData.request}
          apiResponseData={apiDemoData.response}
          sdkCode={sdkDemoCode}
          cliCommand="zk-physics simulate --config simulation.json --verify --output results.json"
          accentColor="#2c3e50"
        />
        
        <SectionTitle>Core Components</SectionTitle>
        <Grid>
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <CardTitle>Physics Core</CardTitle>
            <CardContent>
              High-performance computational engine for real-time physics simulations,
              supporting rigid body dynamics, fluid dynamics, particle systems, soft body physics,
              and complex material interactions with unprecedented accuracy.
            </CardContent>
          </Card>
          
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <CardTitle>Formal Verification Layer</CardTitle>
            <CardContent>
              Mathematical proof system that ensures simulation correctness under
              all possible conditions. The verification layer validates that physics calculations
              conform to established physical laws with cryptographic certainty.
            </CardContent>
          </Card>
          
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <CardTitle>Cryptographic State Tracking</CardTitle>
            <CardContent>
              Secure logging and validation of simulation states that creates a tamper-proof
              history of events. This allows for auditing, replay, and verification of
              simulations even after they have completed.
            </CardContent>
          </Card>
        </Grid>
        
        <SectionTitle>Key Features</SectionTitle>
        <Grid>
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <CardTitle>Military-Grade Precision</CardTitle>
            <CardContent>
              Engineered to meet the strict standards required for defense simulations,
              with error tolerances below 0.001%. Mathematical models validated against
              real-world experimental data across multiple physical domains.
            </CardContent>
          </Card>
          
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <CardTitle>Real-Time Scheduler</CardTitle>
            <CardContent>
              Adaptive computational resource management ensures consistent performance
              even with fluctuating system loads. The scheduler prioritizes critical
              physics calculations while maintaining visual fidelity.
            </CardContent>
          </Card>
          
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <CardTitle>Energy Efficiency</CardTitle>
            <CardContent>
              Optimized algorithms reduce computational overhead by up to 70%
              compared to traditional physics engines. GPU acceleration and
              specialized instruction sets maximize performance while minimizing
              power consumption.
            </CardContent>
          </Card>
          
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <CardTitle>Fault Injection Testing</CardTitle>
            <CardContent>
              Built-in systems for validating simulation robustness under adverse
              conditions. The engine can intentionally introduce controlled errors
              to verify that verification systems properly detect and report them.
            </CardContent>
          </Card>
        </Grid>
        
        <SectionTitle>Applications</SectionTitle>
        <ul>
          <li>Military Training Simulations requiring absolute accuracy and replay capabilities</li>
          <li>High-fidelity scientific research in physics, chemistry, and engineering</li>
          <li>Critical infrastructure planning and natural disaster modeling</li>
          <li>Medical simulations for surgical planning and tissue interaction</li>
          <li>AAA game development requiring both performance and simulation integrity</li>
          <li>Autonomous vehicle testing with verifiable collision detection</li>
          <li>Aerospace and aviation training with certified flight dynamics</li>
        </ul>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Founder</SectionTitle>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <TeamMember
            name="Umesh Adhikari"
            role="Founder & Lead Architect"
            image="/assets/images/team/umesh-adhikari.jpg"
            bio="Umesh Adhikari is a visionary technologist, philosopher, and self-taught innovator, forging new frontiers where mathematics, artificial intelligence, and decentralized systems converge. With roots grounded in determination and a spirit shaped by resilience, Umesh embodies the rare balance of humility and brilliance. From the bustling kitchens of his early days to the deep silence of code and theory, his journey is a testament to transformation — not just personal, but generational. Fueled by curiosity and an unshakable belief in building what the world has yet to imagine, Umesh is the mind behind frameworks like the Advanced Unified Intelligence Framework (AUIF) and Transition Numbers Theory — pioneering languages of logic that challenge the boundaries of science, computation, and consciousness itself."
            tags={['Physics Simulation', 'Zero-Knowledge Proofs', 'Formal Verification']}
            socials={[
              { platform: 'github', url: 'https://github.com/GlobalSushrut' },
              { platform: 'linkedin', url: '#' },
              { platform: 'email', url: 'mailto:umeshlamton@gmail.com' }
            ]}
            accentColor="#2c3e50"
          />
        </div>
      </ContentSection>
      
      <ContentSection>
        <SectionTitle>Documentation</SectionTitle>
        <DocsNavigator 
          sections={[
            { 
              title: 'Documentation', 
              pages: [
                { id: 'overview', title: 'Overview', content: docSections.overview.content },
                { id: 'architecture', title: 'Architecture', content: docSections.architecture.content },
                { id: 'installation', title: 'Installation', content: docSections.installation.content },
                { id: 'usage', title: 'Usage', content: docSections.usage.content }
              ]
            }
          ]}
          initialPage={{ id: 'overview', title: 'Overview', content: docSections.overview.content }}
          accentColor="#2c3e50"
        />
      </ContentSection>
    </PageContainer>
  );
};

export default ZKPhysics;
